import FormattedMessage from 'I18n/components/FormattedMessage';
import { useEffect, useRef } from 'react';
import { PublicAeoStandalonePromoCard } from '../../components/aeo-promo-card/PublicAeoStandalonePromoCard';
import { PublicBannerSection } from '../../components/banners/PublicBannerSection';
import { PublicBannerSkeleton } from '../../components/banners/PublicBannerSkeleton';
import { MARKETING_HUB_PAGE } from '../../components/error-boundary/ErrorBoundaryNames';
import { PublicFaqsSection } from '../../components/faqs/PublicFaqsSection';
import { LoadablePublicProductFeatureTable } from '../../components/feature-table/PublicProductFeatureTable.loadable';
import { PublicCalculatorAnchorLink } from '../../components/header/PublicCalculatorAnchorLink';
import { PublicHubPageHeader } from '../../components/header/PublicHubPageHeader';
import { LoadablePublicHubCreditsExplainer } from '../../components/hub-credits-explainer/PublicHubCreditsExplainer.loadable';
import { PublicIntegrationsSection } from '../../components/integrations/PublicIntegrationsSection';
import { PublicMerchCardCTAs } from '../../components/merch-card/slots/PublicMerchCardCTAs';
import { PublicHubMerchCards } from '../../components/merch-card/PublicHubMerchCards';
import { PublicPageWrapper } from '../../components/PublicPageWrapper';
import PublicPhoneSupportFooter from '../../components/PublicPhoneSupportFooter';
import PublicPhoneSupportMiddle from '../../components/PublicPhoneSupportMiddle';
import { PublicPricingPlanToggleButton } from '../../components/PublicPricingPlanToggleButton';
import { usePublicBannerForPage } from '../../hooks/usePublicBannerForPage';
import { usePublicDocumentTitle } from '../../hooks/usePublicDocumentTitle';
import { usePublicPageUrlState } from '../../hooks/usePublicPageUrlState';
import { usePublicProductGroupsData } from '../../hooks/usePublicProductGroupsData';
import { PublicPageSchema } from '../../schema/PublicPageSchema';
import { Screens } from '../../types/PublicNavItem';
import { TRACKING_SOURCES, trackClickedLink, trackPageView, trackSectionViewed } from '../../utils/tracking';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PRODUCT_GROUP = 'MARKETING';
const SCREEN = Screens.MARKETING;
const AEO_PRODUCT_URL = 'https://www.hubspot.com/products/aeo';
function getPublicMotions(motions) {
  return motions.filter(m => typeof m === 'string');
}
export default function PublicMarketingHubPage() {
  var _aeoProduct$available, _aeoProduct$available2;
  const {
    selectedTerm,
    setSelectedTerm,
    isEnterpriseTier,
    onTierToggle
  } = usePublicPageUrlState();
  const {
    rawData,
    cards,
    schemaProducts,
    cardCount,
    status,
    currencyCode,
    hasStarterTier,
    starterApiName,
    professionalApiName,
    enterpriseApiName,
    standaloneProducts
  } = usePublicProductGroupsData(PRODUCT_GROUP, {
    selectedTerm,
    isEnterpriseTier
  });
  const {
    hasBanner,
    bannerProps
  } = usePublicBannerForPage(rawData, SCREEN);
  usePublicDocumentTitle(SCREEN);
  const hasTrackedPageViewRef = useRef(false);
  useEffect(() => {
    if (!hasTrackedPageViewRef.current) {
      hasTrackedPageViewRef.current = true;
      trackPageView({
        screen: SCREEN
      });
    }
  }, []);
  const aeoProduct = standaloneProducts[0];
  const aeoMonthlyPrice = aeoProduct === null || aeoProduct === void 0 || (_aeoProduct$available = aeoProduct.availableTerms['MONTHLY']) === null || _aeoProduct$available === void 0 ? void 0 : _aeoProduct$available.monthlyPrice;
  const aeoAnnualPrice = aeoProduct === null || aeoProduct === void 0 || (_aeoProduct$available2 = aeoProduct.availableTerms['ANNUAL']) === null || _aeoProduct$available2 === void 0 ? void 0 : _aeoProduct$available2.monthlyPrice;
  return /*#__PURE__*/_jsxs(PublicPageWrapper, {
    errorBoundaryName: MARKETING_HUB_PAGE,
    navMarkerName: "MARKETING_PAGE_V2",
    metricsRoute: SCREEN,
    chunkName: "public-marketing-hub-page",
    children: [status === 'ready' && /*#__PURE__*/_jsx(PublicPageSchema, {
      productGroup: PRODUCT_GROUP,
      cards: schemaProducts,
      currencyCode: currencyCode
    }), /*#__PURE__*/_jsx(PublicBannerSkeleton, {
      isLoading: status === 'loading',
      children: hasBanner && /*#__PURE__*/_jsx(PublicBannerSection, Object.assign({}, bannerProps))
    }), aeoProduct && /*#__PURE__*/_jsx(PublicAeoStandalonePromoCard, {
      monthlyPrice: aeoMonthlyPrice != null ? Number(aeoMonthlyPrice) : null,
      annualPrice: aeoAnnualPrice != null ? Number(aeoAnnualPrice) : null,
      currencyCode: currencyCode,
      priceLoading: status === 'loading',
      purchaseMotions: /*#__PURE__*/_jsx(PublicMerchCardCTAs, {
        card: aeoProduct,
        currencyCode: currencyCode,
        paymentTerm: selectedTerm,
        commitmentTerm: selectedTerm,
        motions: getPublicMotions(aeoProduct.purchaseMotions),
        screen: SCREEN,
        layout: "row"
      }),
      onSectionViewed: () => trackSectionViewed({
        name: TRACKING_SOURCES.AEO_STANDALONE_PROMO_CARD,
        screen: SCREEN
      }),
      onLearnMoreClick: () => trackClickedLink({
        type: 'external',
        url: AEO_PRODUCT_URL,
        name: 'learn-about-aeo',
        trackingSource: SCREEN,
        screen: SCREEN
      })
    }), /*#__PURE__*/_jsx(PublicHubPageHeader, {
      productGroup: PRODUCT_GROUP,
      subtitles: [/*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "publicPricingPage.header.marketing.subtitle"
        }), professionalApiName && /*#__PURE__*/_jsx(PublicCalculatorAnchorLink, {
          defaultProductApiName: professionalApiName,
          onClick: () => trackClickedLink({
            name: 'calculate-your-price',
            url: '#calculator',
            type: 'internal',
            trackingSource: SCREEN,
            screen: SCREEN
          })
        })]
      })]
    }), /*#__PURE__*/_jsx(PublicHubMerchCards, {
      cards: cards,
      productGroup: PRODUCT_GROUP,
      status: status,
      currencyCode: currencyCode,
      selectedTerm: selectedTerm,
      setSelectedTerm: setSelectedTerm,
      skeletonCount: cardCount,
      screen: SCREEN,
      belowCards: hasStarterTier ? /*#__PURE__*/_jsx(PublicPricingPlanToggleButton, {
        isEnterpriseTier: isEnterpriseTier,
        onToggle: onTierToggle
      }) : undefined
    }), /*#__PURE__*/_jsx(LoadablePublicHubCreditsExplainer, {
      screen: SCREEN,
      cards: cards,
      currencyCode: currencyCode,
      term: selectedTerm,
      starterApiName: starterApiName,
      professionalApiName: professionalApiName,
      enterpriseApiName: enterpriseApiName
    }), /*#__PURE__*/_jsx(LoadablePublicProductFeatureTable, {
      productGroup: PRODUCT_GROUP,
      productGroupResponse: rawData,
      currencyCode: currencyCode,
      selectedTerm: selectedTerm,
      screen: SCREEN
    }), /*#__PURE__*/_jsx(PublicPhoneSupportMiddle, {}), /*#__PURE__*/_jsx(PublicIntegrationsSection, {
      productGroup: PRODUCT_GROUP,
      screen: SCREEN
    }), /*#__PURE__*/_jsx(PublicFaqsSection, {
      productGroup: PRODUCT_GROUP,
      screen: SCREEN
    }), /*#__PURE__*/_jsx(PublicPhoneSupportFooter, {})]
  });
}